import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from 'react';
import { cn } from '../lib/cn';
import { useFieldProps } from '../lib/field-context';
import { focusRing } from '../lib/focus-ring';
import { FIELD_GROUP_TONE, type FieldGroup } from '../lib/identity';
import { FIELD_CHIP_CLASS } from './FieldChip';
import { Tag } from './Tag';

export interface SelectOption {
  /**
   * The stored value. Must not be the empty string — Radix reserves `''` for
   * "show the placeholder", so a real option using it can never be selected.
   * Use an explicit sentinel such as `'__skip__'` for a "do not import" choice.
   */
  value: string;
  label: string;
  /** Second line under the label in the list. Not shown in the trigger. */
  description?: ReactNode;
  disabled?: boolean;
}

const triggerSizeClass = {
  sm: 'px-3 py-1.5 text-[0.85rem]',
  md: 'px-3.5 py-2.5 text-[0.95rem]',
} as const;

export type SelectSize = keyof typeof triggerSizeClass;

export interface SelectProps
  extends Omit<ComponentPropsWithoutRef<typeof SelectPrimitive.Root>, 'children'> {
  options: readonly SelectOption[];
  /** Shown when nothing is selected. */
  placeholder?: string;
  size?: SelectSize;
  /** Force the error styling without a surrounding `<FormField>`. */
  invalid?: boolean;
  id?: string;
  /** Accessible name when there is no visible `<Label>` — e.g. in a table row. */
  'aria-label'?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  /** Classes for the trigger. */
  className?: string;
  /** Classes for the popover. */
  contentClassName?: string;
  /**
   * Default `'field'`, the form control. `'chip'` (slice 66) draws the trigger
   * as a `FieldChip` — group tag, value, chevron — for a select whose value IS
   * a contact field, such as an upload's column mapping. Only the trigger's
   * skin changes: Radix's listbox, keyboard model and items are the same.
   */
  appearance?: 'field' | 'chip';
  /** The field group whose tag a chip trigger shows. Default `'profile'`. */
  chipGroup?: FieldGroup;
}

/**
 * A styled Radix Select. Inside a `<FormField>` it picks up the field's id,
 * `aria-describedby` and invalid state automatically, exactly like `Input`.
 *
 *     <Select
 *       value={mapping[column] ?? SKIP}
 *       onValueChange={(value) => setMapping(column, value)}
 *       options={targets}
 *       aria-label={`Map column ${column}`}
 *     />
 *
 * Radix handles the listbox keyboard model: type-ahead, arrows, Home/End,
 * Escape, and focus returning to the trigger on close.
 */
export const Select = forwardRef<HTMLButtonElement, SelectProps>(function Select(
  {
    options,
    placeholder = 'Select…',
    size = 'md',
    invalid,
    id,
    className,
    contentClassName,
    appearance = 'field',
    chipGroup = 'profile',
    'aria-label': ariaLabel,
    'aria-describedby': describedBy,
    'aria-invalid': ariaInvalid,
    ...rest
  },
  ref,
) {
  const fieldProps = useFieldProps({
    id,
    'aria-describedby': describedBy,
    'aria-invalid': ariaInvalid ?? (invalid === true ? true : undefined),
  });

  return (
    <SelectPrimitive.Root {...rest}>
      <SelectPrimitive.Trigger
        ref={ref}
        aria-label={ariaLabel}
        className={appearance === 'chip' ? cn(
          FIELD_CHIP_CLASS,
          'cursor-pointer text-left hover:bg-brand-a/24 data-[state=open]:bg-brand-a/24',
          'disabled:cursor-not-allowed disabled:opacity-55',
          'aria-[invalid=true]:border-bad',
          focusRing,
          className,
        ) : cn(
          'flex w-full cursor-pointer items-center justify-between gap-2',
          'rounded-base border border-line-strong bg-ink/55 text-left leading-normal text-text',
          'transition-[background-color,border-color,box-shadow] duration-150 ease-wend',
          'hover:border-white/25',
          'focus-visible:border-brand-a focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-b',
          'disabled:cursor-not-allowed disabled:opacity-55',
          'data-[placeholder]:text-muted',
          'aria-[invalid=true]:border-bad aria-[invalid=true]:focus-visible:outline-bad',
          triggerSizeClass[size],
          className,
        )}
        {...fieldProps}
      >
        {appearance === 'chip' && <Tag inset tone={FIELD_GROUP_TONE[chipGroup]}>{chipGroup}</Tag>}
        <span className="min-w-0 truncate">
          <SelectPrimitive.Value placeholder={placeholder} />
        </span>
        <SelectPrimitive.Icon asChild>
          <ChevronDown
            aria-hidden="true"
            className={appearance === 'chip' ? 'size-3.5 shrink-0 text-brand-ink' : 'size-4 shrink-0 text-muted'}
          />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>

      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={6}
          // The Blueprint popover (slice 66): a violet hairline, the solid
          // surface-2 rather than a blurred 97% one, the `pop` shadow and the
          // short `pop` entrance — the concept's field picker, which every
          // select now opens like. Items below are unchanged.
          className={cn(
            'z-50 overflow-hidden rounded-base border border-brand-b/45 bg-surface-2 shadow-pop animate-pop',
            'max-h-[min(20rem,var(--radix-select-content-available-height))]',
            'min-w-[var(--radix-select-trigger-width)]',
            contentClassName,
          )}
        >
          <SelectPrimitive.ScrollUpButton className="flex h-6 items-center justify-center text-muted">
            <ChevronUp aria-hidden="true" className="size-3.5" />
          </SelectPrimitive.ScrollUpButton>

          <SelectPrimitive.Viewport className="w-full min-w-[var(--radix-select-trigger-width)] p-1">
            {options.map((option) => (
              <SelectPrimitive.Item
                key={option.value}
                value={option.value}
                disabled={option.disabled}
                className={cn(
                  'relative flex cursor-pointer select-none items-start gap-2 rounded-sm py-2 pl-8 pr-3',
                  'text-[0.9rem] leading-snug text-muted outline-none',
                  'data-[highlighted]:bg-white/[0.07] data-[highlighted]:text-text',
                  'data-[state=checked]:text-text',
                  'data-[disabled]:pointer-events-none data-[disabled]:opacity-45',
                )}
              >
                <span className="absolute left-2.5 top-2.5 flex size-3.5 items-center justify-center">
                  <SelectPrimitive.ItemIndicator>
                    <Check aria-hidden="true" className="size-3.5 text-brand-b" />
                  </SelectPrimitive.ItemIndicator>
                </span>

                <span className="flex min-w-0 flex-col">
                  <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
                  {option.description !== undefined && (
                    <span className="mt-0.5 text-[0.75rem] leading-snug text-muted">
                      {option.description}
                    </span>
                  )}
                </span>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>

          <SelectPrimitive.ScrollDownButton className="flex h-6 items-center justify-center text-muted">
            <ChevronDown aria-hidden="true" className="size-3.5" />
          </SelectPrimitive.ScrollDownButton>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
});
